// KPIsContainer.tsx
import React from "react";
import styled from "styled-components";
import { Journey } from "../../utils/mapJourneysData";
import KPIBox from "./KPI";
import { getColor, getReliabilityColorGradient } from "../../utils/colorFormatter";
import { Timer, XCircle, ArrowRightLeft } from 'lucide-react';
import happytrain from '../../icons/chuuchuu-icon.svg';
import neutraltrain from '../../icons/chuuchuu-neutral.svg';
import sadtrain from '../../icons/chuuchuu-sad.svg';
import { StyledTooltip, TooltipGlobalStyle, TooltipContentWrapper, TooltipMainText, TooltipSecondaryText } from '../TooltipStyles';
import { useTranslation } from 'react-i18next';


const Container = styled('div')`
display: flex;
align-items: flex-start;
align-content: flex-start;
gap: 1rem;
align-self: stretch;
flex-wrap: wrap;

@media (min-width: 768px) {
  gap: 1rem;
}
`;

type KPIsContainerProps = {
  journey: Journey;
};

const KPIsContainer: React.FC<KPIsContainerProps> = ({ journey }) => {
  const { t } = useTranslation();
  
  // Extract, round, and default KPIs to null if unavailable
  const reliability = journey.reliability?.reliabilityScore != null
    ? Math.round(journey.reliability.reliabilityScore)
    : null;

  const averageDelayMinutes = journey.journeyDelayStats?.averageDelayAtDest != null
    ? Math.round(journey.journeyDelayStats.averageDelayAtDest / 60)
    : null;

  const cancellationRate = journey.journeyDelayStats?.journeyCancellationRate != null
    ? Math.round(journey.journeyDelayStats.journeyCancellationRate)
    : null;

    const missedTransfers = journey.transferStats?.length
    ? (() => {
        // Extract and filter valid percentageMissedConnections values
        const validConnections = journey.transferStats
          .filter(stats => stats !== null)
          .map(stats => stats!.percentageMissedConnections);
  
        // Return the rounded maximum if there are valid values, else null
        return validConnections.length > 0
          ? Math.round(Math.max(...validConnections))
          : null;
      })()
    : null;

  // Define color conditions using the existing colorFormatter
  const reliabilityColor = (value: number) => getReliabilityColorGradient(value);
  const delayColor = (value: number) => getColor(value, 'delayInMinutes');
  const cancellationColor = (value: number) => getColor(value, 'cancellationPercentage');
  const transferColor = (value: number) => getColor(value, 'missedTransfersPercentage');

  // Function to choose the correct train icon based on reliability value
  const getReliabilityIcon = (value: number) => {
    if (value === -1) return neutraltrain;
    if (value >= 75) return happytrain;
    if (value >= 50) return neutraltrain;
    return sadtrain;
  };

  return (
    <>
      <TooltipGlobalStyle />
      <Container>
        {reliability !== null && (
          <div data-tooltip-id="reliability-tooltip">
            <KPIBox
              icon={{ type: 'svg', path: getReliabilityIcon(reliability)}} // Dynamically choose icon based on reliability value
              title={t('journeyDetails.kpis.chuuchuuScore')}
              value={`${reliability}`}
              colorCondition={reliabilityColor}
              isReliability={true}
            />
            <StyledTooltip id="reliability-tooltip" place="top" className="tooltip-styled">
              <TooltipContentWrapper>
                <TooltipMainText>
                  {t('journeyDetails.kpis.reliabilityTooltip')}
                </TooltipMainText>
                <TooltipSecondaryText className="tooltip-secondary-text">
                  {t('journeyDetails.reliabilityScoreBadge.basedOnLast30Days')}
                </TooltipSecondaryText>
              </TooltipContentWrapper>
            </StyledTooltip>
          </div>
        )}
        {averageDelayMinutes !== null && (
          <div data-tooltip-id="average-delay-tooltip">
            <KPIBox
              icon={{ type: 'lucide', icon: Timer }}
              title={t('journeyDetails.kpis.averageDelay')}
              value={`${averageDelayMinutes >= 0 ? `+${averageDelayMinutes}` : `${averageDelayMinutes}`}m`}
              colorCondition={delayColor}
            />
            <StyledTooltip id="average-delay-tooltip" place="top" className="tooltip-styled">
              <TooltipContentWrapper>
                <TooltipMainText>
                  {t('journeyDetails.kpis.averageDelayTooltip')}
                </TooltipMainText>
                <TooltipSecondaryText className="tooltip-secondary-text">
                  {t('journeyDetails.reliabilityScoreBadge.basedOnLast30Days')}
                </TooltipSecondaryText>
              </TooltipContentWrapper>
            </StyledTooltip>
          </div>
        )}
        {cancellationRate !== null && (
          <div data-tooltip-id="cancellation-rate-tooltip"
          >
            <KPIBox
              icon={{ type: 'lucide', icon: XCircle }}
              title={t('journeyDetails.kpis.trainsCancelled')}
              value={`${cancellationRate}%`}
              colorCondition={cancellationColor}
            />
            <StyledTooltip id="cancellation-rate-tooltip" place="top" className="tooltip-styled">
              <TooltipContentWrapper>
                <TooltipMainText>
                  {t('journeyDetails.kpis.cancellationRateTooltip')}
                </TooltipMainText>
                <TooltipSecondaryText className="tooltip-secondary-text">
                  {t('journeyDetails.reliabilityScoreBadge.basedOnLast30Days')}
                </TooltipSecondaryText>
              </TooltipContentWrapper>
            </StyledTooltip>
          </div>
        )}
        {missedTransfers !== null && (
          <div data-tooltip-id="missed-transfers-tooltip">
            <KPIBox
              icon={{ type: 'lucide', icon: ArrowRightLeft }}
              title={t('journeyDetails.kpis.missedTransfers')}
              value={`${missedTransfers}%`}
              colorCondition={transferColor}
            />
            <StyledTooltip id="missed-transfers-tooltip" place="top" className="tooltip-styled">
              <TooltipContentWrapper>
                <TooltipMainText>
                  {t('journeyDetails.kpis.missedTransfersTooltip')}
                </TooltipMainText>
                <TooltipSecondaryText className="tooltip-secondary-text">
                  {t('journeyDetails.reliabilityScoreBadge.basedOnLast30Days')}
                </TooltipSecondaryText>
              </TooltipContentWrapper>
            </StyledTooltip>
          </div>
        )}
      </Container>
    </>
  );
};

export default KPIsContainer;
